// @flow
import type { Query } from "common/graphql/types"
import gql from "graphql-tag"
import { groupBy, length } from "ramda"
import type { ID } from "data/enums/types"
import type { DateString } from "data/units/date/types"
import { reservationFragment, reservationMinFragment } from "./fragments"
import type {
  Reservation,
  ReservationStatus,
  ReservationPricing,
  AccessoryQueryInput,
  BuyItPricing,
} from "../types"

export const reservationDetailQuery: Query<Reservation> = {
  gql: gql`
    query reservationDetailQuery($id: Int!) {
      reservation {
        get(id: $id) {
          ...reservationFragment
        }
      }
    }
    ${reservationFragment}
  `,
  selector: ["reservation", "get"],
  options: () => ({
    fetchPolicy: "network-only",
  }),
}

export const reservationListQuery: Query<{
  [ReservationStatus]: Reservation[],
}> = {
  gql: gql`
    query reservationListQuery {
      reservation {
        list {
          data {
            ...reservationMinFragment
          }
        }
      }
    }
    ${reservationMinFragment}
  `,
  // $Ramda
  transform: groupBy(x => x.status),
  selector: ["reservation", "list", "data"],
}

export const reservationCountQuery: Query<number> = {
  gql: gql`
    query reservationCountQuery {
      reservation {
        list(filter: { limit: 9999999, status: confirmed }) {
          data {
            id
          }
        }
      }
    }
  `,
  transform: length,
  selector: ["reservation", "list", "data"],
}

export type ReservationPricingQueryInput = {|
  productVariantId: ID,
  affiliateId: ID,
  start: DateString,
  end: DateString,
  accessories?: AccessoryQueryInput[],
  subscriptionSlug?: string,
|}

export type ExtendReservationPricingQueryInput = {|
  reservationId: ID,
  end: DateString,
  accessories?: AccessoryQueryInput,
|}

export const reservationPricingQuery: Query<
  ReservationPricing,
  {|
    input: ReservationPricingQueryInput,
  |},
> = {
  gql: gql`
    query reservationPricingQuery($input: PriceInput!) {
      reservation {
        getPrice(input: $input) {
          totalPrice
          fullPrice
          totalDays
          prices
          currency
          discounts {
            percentage
            type
            detail
            limitDays
          }
          accessories {
            name
            prices
          }
        }
      }
    }
  `,
  selector: ["reservation", "getPrice"],
}

export const reservationExtensionPricingQuery: Query<
  ReservationPricing,
  {|
    input: ExtendReservationPricingQueryInput,
  |},
> = {
  gql: gql`
    query reservationExtensionPricingQuery($input: ExtensionPriceInput!) {
      reservation {
        getExtensionPrice(input: $input) {
          totalPrice
          fullPrice
          totalDays
          prices
          currency
          discounts {
            percentage
            type
            detail
            limitDays
          }
          accessories {
            name
            prices
          }
        }
      }
    }
  `,
  selector: ["reservation", "getExtensionPrice"],
}

export const reservationBuyItPricingQuery: Query<
  BuyItPricing | null,
  { reservationId: number },
> = {
  gql: gql`
    query reservationBuyItPricingQuery($reservationId: Int!) {
      reservation {
        getBuyItPrice(reservationId: $reservationId) {
          currency
          rrpPrice
          proceedsAmount
          discountedPrice
        }
      }
    }
  `,
  selector: ["reservation", "getBuyItPrice"],
  options: () => ({
    fetchPolicy: "network-only",
  }),
}



// WEBPACK FOOTER //
// ./src/data/reservations/graphql/queries.js